import React from 'react';
import type { Tone } from '../../domain/types';

export interface PillProps extends React.HTMLAttributes<HTMLSpanElement> {
  label?: React.ReactNode;
  tone?: Tone | 'neutral';
  pulse?: boolean;
  count?: number | string;
  size?: 'xs' | 'sm' | 'md';
}

export const Pill: React.FC<PillProps> = ({
  children,
  label,
  tone = 'accent',
  pulse = false,
  count,
  size = 'sm',
  className = '',
  style,
  ...props
}) => {
  const getColors = (t: Tone | 'neutral') => {
    switch (t) {
      case 'ok':
        return { bg: 'var(--z-ok-bg)', text: 'var(--z-ok-ink, var(--z-ok))', dot: 'var(--z-ok)' };
      case 'warn':
        return { bg: 'var(--z-warn-bg)', text: 'var(--z-warn-ink, var(--z-warn))', dot: 'var(--z-warn)' };
      case 'bad':
        return { bg: 'var(--z-bad-bg)', text: 'var(--z-bad-ink, var(--z-bad))', dot: 'var(--z-bad)' };
      case 'accent':
        return { bg: 'var(--z-accent-soft)', text: 'var(--z-accent-copy)', dot: 'var(--z-accent)' };
      case 'flat':
      case 'neutral':
      default:
        return { bg: 'var(--z-fill-2)', text: 'var(--z-muted-2)', dot: 'var(--z-muted)' };
    }
  };

  const colors = getColors(tone);

  const sizeStyles: Record<'xs' | 'sm' | 'md', { padding: string; fontSize: string; height: string; dotSize: string }> = {
    xs: { padding: '1px 5px', fontSize: '10px', height: '16px', dotSize: '4px' },
    sm: { padding: '3px 9px', fontSize: '10.5px', height: '20px', dotSize: '5px' },
    md: { padding: '3px 10px', fontSize: '12px', height: '24px', dotSize: '6px' }
  };

  const currentSize = sizeStyles[size] || sizeStyles.sm;

  const pillStyle: React.CSSProperties = {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '5px',
    borderRadius: '7px',
    backgroundColor: colors.bg,
    color: colors.text,
    fontWeight: 700,
    lineHeight: 1.2,
    whiteSpace: 'nowrap',
    userSelect: 'none',
    padding: currentSize.padding,
    fontSize: currentSize.fontSize,
    ...style
  };

  return (
    <span
      className={`z-pill z-pill-${tone} z-pill-${size} ${className}`}
      style={pillStyle}
      {...props}
    >
      {pulse && (
        <span
          style={{
            width: currentSize.dotSize,
            height: currentSize.dotSize,
            borderRadius: '50%',
            backgroundColor: colors.dot,
            flexShrink: 0,
            animation: 'zpulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite'
          }}
          aria-hidden="true"
        />
      )}
      {label ?? children}
      {count !== undefined && (
        <span style={{ opacity: 0.75, fontFamily: 'var(--font-mono)' }}>{count}</span>
      )}
    </span>

  );
};
